import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { LogOut, ScrollText, HelpingHand, Menu, X } from "lucide-react";

function BrandLogo({ to }) {
  return (
    <Link to={to} className="flex items-center gap-2 sm:gap-2.5 min-w-0" data-testid="app-logo">
      <div
        className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
        style={{ background: "var(--action)" }}
      >
        <ScrollText className="w-4 h-4 text-white" />
      </div>
      <div className="leading-tight min-w-0">
        <div className="font-display font-bold text-[14px] sm:text-[15px] truncate" style={{ color: "var(--text-primary)" }}>
          DS-260USA<span style={{ color: "var(--action)" }}>.com</span>
        </div>
        <div className="text-[10px] sm:text-[10.5px] uppercase tracking-wider hidden xs:block" style={{ color: "var(--text-secondary)" }}>
          Consular Processing
        </div>
      </div>
    </Link>
  );
}

function DesktopNav({ user, onLogout }) {
  return (
    <div className="hidden md:flex items-center gap-3">
      <Link
        to="/directory"
        data-testid="header-request-assistance-btn"
        className="btn-ghost text-sm flex items-center gap-1.5"
        style={{
          border: "1px solid var(--ai-border)",
          background: "var(--ai-bg)",
          color: "var(--ai-text)",
        }}
        title="Browse attorneys and form-filler assistants"
      >
        <HelpingHand className="w-4 h-4" /> Request Assistance
      </Link>
      <Link to="/account" className="text-right leading-tight" data-testid="header-account-link" title="Account security">
        <div className="text-[13px] font-medium" style={{ color: "var(--text-primary)" }}>
          {user.name}
        </div>
        <div className="text-[11px]" style={{ color: "var(--text-secondary)" }}>{user.email}</div>
      </Link>
      <button
        data-testid="header-logout-btn"
        onClick={onLogout}
        className="btn-ghost text-sm flex items-center gap-1.5"
        title="Sign out"
      >
        <LogOut className="w-4 h-4" /> Sign out
      </button>
    </div>
  );
}

function MobileMenu({ user, onClose, onLogout }) {
  return (
    <>
      <button
        type="button"
        aria-label="Close menu"
        className="md:hidden fixed inset-0 z-40"
        style={{ background: "rgba(26,46,36,0.55)" }}
        onClick={onClose}
      />
      <div
        className="md:hidden fixed top-0 right-0 z-40 h-full w-[82%] max-w-xs card-surface shadow-2xl"
        style={{ paddingTop: "env(safe-area-inset-top)", borderRadius: "18px 0 0 18px" }}
        data-testid="app-shell-mobile-menu"
      >
        <div className="p-5 flex items-center justify-between border-b" style={{ borderColor: "var(--border-stone)" }}>
          <div className="leading-tight min-w-0">
            <div className="text-sm font-semibold truncate" style={{ color: "var(--text-primary)" }}>{user.name}</div>
            <div className="text-xs truncate" style={{ color: "var(--text-secondary)" }}>{user.email}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded hover:bg-black/5"
            aria-label="Close menu"
            data-testid="app-shell-mobile-close-btn"
          >
            <X className="w-5 h-5" style={{ color: "var(--text-secondary)" }} />
          </button>
        </div>
        <div className="p-3 space-y-1">
          <Link
            to="/dashboard"
            onClick={onClose}
            className="stage-item"
            data-testid="mobile-nav-dashboard"
          >
            <div className="dot" />
            <div>All cases</div>
          </Link>
          <Link
            to="/directory"
            onClick={onClose}
            className="stage-item"
            data-testid="mobile-nav-directory"
          >
            <HelpingHand className="w-4 h-4" style={{ color: "var(--ai-text)" }} />
            <div>Request Assistance</div>
          </Link>
          <button
            type="button"
            onClick={onLogout}
            className="stage-item w-full text-left"
            data-testid="mobile-nav-logout"
          >
            <LogOut className="w-4 h-4" />
            <div>Sign out</div>
          </button>
        </div>
      </div>
    </>
  );
}

export default function AppShell({ children, showNav = true }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const signOut = async () => { setMobileOpen(false); await logout(); nav("/login"); };
  return (
    <div className="min-h-screen bg-grain" style={{ background: "var(--bg-app)" }}>
      <header
        className="border-b sticky top-0 z-30 backdrop-blur-md"
        style={{
          borderColor: "var(--border-stone)",
          background: "rgba(249,248,245,0.85)",
          paddingTop: "env(safe-area-inset-top)",
        }}
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-2.5 sm:py-3 flex items-center justify-between gap-2">
          <BrandLogo to={user ? "/dashboard" : "/"} />
          {showNav && user && (
            <>
              <DesktopNav user={user} onLogout={signOut} />
              <button
                type="button"
                data-testid="app-shell-mobile-menu-btn"
                onClick={() => setMobileOpen(true)}
                className="md:hidden p-2 rounded-lg"
                style={{ border: "1px solid var(--border-stone)", background: "#fff" }}
                aria-label="Open menu"
              >
                <Menu className="w-5 h-5" style={{ color: "var(--text-primary)" }} />
              </button>
            </>
          )}
        </div>
      </header>

      {showNav && user && mobileOpen && (
        <MobileMenu user={user} onClose={() => setMobileOpen(false)} onLogout={signOut} />
      )}

      {children}
    </div>
  );
}
